import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import React, { useRef, useState, useMemo, useImperativeHandle, forwardRef } from 'react';
import { useComponentTheme, CheckmarkIcon } from '@chegg-ui/theme';
import { throttle, useLayoutEffectIsomorphic } from '@chegg-ui/utils';
import { CheckIcon } from '@chegg-ui/horizon-icons';
import { createPortal } from 'react-dom';
import { ListboxOptions, ListboxItem, ListboxContainer, getThemeConfig, CheckmarkIconPositioner } from '../styles';
var Listbox = /*#__PURE__*/forwardRef(function (_ref, forwardedRef) {
  var inputId = _ref.inputId,
      options = _ref.options,
      isOpen = _ref.isOpen,
      pseudoFocusedIndex = _ref.pseudoFocusedIndex,
      selectedIndex = _ref.selectedIndex,
      onSelectOption = _ref.onSelectOption,
      _ref$maxHeight = _ref.maxHeight,
      maxHeight = _ref$maxHeight === void 0 ? '200px' : _ref$maxHeight,
      className = _ref.className,
      dataTest = _ref['data-test'],
      rendererRef = _ref.rendererRef,
      container = _ref.container,
      _ref$showCheckIcon = _ref.showCheckIcon,
      showCheckIcon = _ref$showCheckIcon === void 0 ? true : _ref$showCheckIcon,
      theme = _ref.theme,
      parentSpecificProps = _objectWithoutProperties(_ref, ["inputId", "options", "isOpen", "pseudoFocusedIndex", "selectedIndex", "onSelectOption", "maxHeight", "className", "data-test", "rendererRef", "container", "showCheckIcon", "theme"]);

  var componentTheme = useComponentTheme({
    name: 'Listbox',
    theme: getThemeConfig(theme)
  });
  var optionsListRef = useRef(null);
  /* istanbul ignore next */

  useImperativeHandle(forwardedRef, function () {
    return optionsListRef.current;
  });
  var focusedOptionRef = useRef(null);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isMouseHovered = _useState2[0],
      setIsMouseHovered = _useState2[1]; // removing hover styles when using keyboard arrows


  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      isDropup = _useState4[0],
      setIsDropup = _useState4[1];

  var _useState5 = useState(),
      _useState6 = _slicedToArray(_useState5, 2),
      rendererBoundingRect = _useState6[0],
      setRendererBoundingRect = _useState6[1];

  var _useState7 = useState(0),
      _useState8 = _slicedToArray(_useState7, 2),
      listboxHeight = _useState8[0],
      setListboxHeight = _useState8[1];
  /*
   * determine if the listbox should drop down or up, according to screen space available
   * only called in response to the user action of opening the listbox, hence safe for SSR
   */


  useLayoutEffectIsomorphic(function () {
    var _rendererRef$current;

    /* istanbul ignore next */
    if (!optionsListRef.current) return;
    var boundingRect = optionsListRef.current.getBoundingClientRect();
    var rendererBoundingRect = rendererRef === null || rendererRef === void 0 ? void 0 : (_rendererRef$current = rendererRef.current) === null || _rendererRef$current === void 0 ? void 0 : _rendererRef$current.getBoundingClientRect();
    var windowHeight = window.innerHeight;
    var listHeight = boundingRect.height;
    var boundingRectTop = rendererBoundingRect ? rendererBoundingRect.top : boundingRect.top;
    var spaceAboveDropdown = boundingRectTop;
    var spaceBelowDropdown = windowHeight - boundingRectTop;
    var shouldDropup = spaceBelowDropdown < listHeight && spaceAboveDropdown > spaceBelowDropdown;
    setIsDropup(shouldDropup);
    setListboxHeight(listHeight);
    setRendererBoundingRect(rendererBoundingRect);
    var updateRendererBoundingRect = throttle(function () {
      /* istanbul ignore next */
      if (!(rendererRef !== null && rendererRef !== void 0 && rendererRef.current) || !isOpen) return;
      setRendererBoundingRect(rendererRef.current.getBoundingClientRect());
    }, 0);
    window.addEventListener('scroll', updateRendererBoundingRect);
    return function () {
      window.removeEventListener('scroll', updateRendererBoundingRect);
    };
  }, [rendererRef, isOpen]);
  /*
   * scroll focused list option fully into view if needed.
   * only called in response to the user action of opening the listbox or arrowing through, hence safe for SSR
   */

  useLayoutEffectIsomorphic(function () {
    if (isOpen) {
      /* istanbul ignore else */
      if (focusedOptionRef.current) {
        // IE/Safari don't support ScrollIntoViewOptions so we need this check
        // https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollIntoView
        focusedOptionRef.current.scrollIntoView({
          block: 'nearest'
        });
      }

      setIsMouseHovered(false);
    }
  }, [pseudoFocusedIndex, isOpen]);
  var listbox = /*#__PURE__*/React.createElement(ListboxContainer, {
    "data-test": "".concat(dataTest ? dataTest : inputId, "_listboxcontainer"),
    isOpen: isOpen && !!options.length,
    dropup: isDropup,
    className: className,
    rendererBoundingRect: rendererBoundingRect,
    listboxHeight: listboxHeight,
    container: container,
    customTheme: componentTheme
  }, /*#__PURE__*/React.createElement(ListboxOptions, _extends({
    id: "".concat(inputId, "_listbox"),
    role: "listbox",
    ref: optionsListRef,
    maxHeight: maxHeight,
    "aria-labelledby": "".concat(inputId, "_label"),
    "aria-activedescendant": parentSpecificProps.tabIndex && pseudoFocusedIndex >= 0 ? "".concat(inputId, "_option_").concat(pseudoFocusedIndex) : undefined,
    "data-test": dataTest,
    customTheme: componentTheme
  }, parentSpecificProps), useMemo(function () {
    return (// this wacky typing is to deal with https://stackoverflow.com/a/49511416
      options.map(function (option, index) {
        var isPsuedoFocused = index === pseudoFocusedIndex;
        var isSelected = index === selectedIndex;
        return /*#__PURE__*/React.createElement(ListboxItem, {
          customTheme: componentTheme,
          key: "".concat(inputId, "_option_").concat(index),
          id: "".concat(inputId, "_option_").concat(index),
          ref: isPsuedoFocused ? focusedOptionRef : null,
          isFocused: isPsuedoFocused,
          "aria-selected": isPsuedoFocused || undefined,
          role: "option",
          isKeyboardHover: !isMouseHovered,
          isSelected: isSelected,
          value: typeof option === 'string' ? option : option.value,
          onMouseDown: function onMouseDown() {
            onSelectOption(index);
          },
          onMouseMove: function onMouseMove() {
            setIsMouseHovered(true);
          }
        }, isSelected && /*#__PURE__*/React.createElement(CheckmarkIconPositioner, {
          customTheme: componentTheme
        }, componentTheme.showHorizonIcon ? showCheckIcon ? /*#__PURE__*/React.createElement(CheckIcon, {
          size: "small"
        }) : '' : /*#__PURE__*/React.createElement(CheckmarkIcon, {
          size: "small"
        })), typeof option === 'string' ? option : option.child);
      })
    );
  }, [options, selectedIndex, pseudoFocusedIndex, isMouseHovered])));
  return container ? /*#__PURE__*/createPortal(listbox, container) : listbox;
});
Listbox.displayName = 'Listbox';
export default Listbox;